import { extractTsMetaFromNotes } from './supplementaryTax';

const DEFAULT_TARIF_POIDS = {
  weightCeilingTon: 20,
  ouvrable: { minimumHt: 4499, rateHtPerTon: 153 },
  special: { minimumHt: 9000, rateHtPerTon: 302 }
};
const DEFAULT_TVA_RATE = 0.18;

function toDateKey(d) {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

/** Same as server loadTariffInstant: marchandise pass time, else scheduled loadDate. */
export function loadTariffInstant(loadLike) {
  if (loadLike?.loadedWeighedAt != null) {
    return new Date(loadLike.loadedWeighedAt);
  }
  if (loadLike?.loadDate != null) {
    return new Date(loadLike.loadDate);
  }
  return new Date();
}

export function isJoursOuvrables(loadDate, holidayDateSet) {
  const d = new Date(loadDate);
  if (Number.isNaN(d.getTime())) return false;
  const day = d.getDay();
  const hour = d.getHours();
  const isWeekday = day >= 1 && day <= 5;
  const isWorkingHour = hour >= 8 && hour < 16;
  const isHoliday = holidayDateSet && holidayDateSet.has(toDateKey(d));
  return isWeekday && isWorkingHour && !isHoliday;
}

function calculateMontantHt(netWeightKg, loadDate, holidayDateSet, tarifPoids) {
  const netTon = Number(netWeightKg || 0) / 1000;
  if (!Number.isFinite(netTon) || netTon <= 0) return 0;

  const tarif_ = tarifPoids || DEFAULT_TARIF_POIDS;
  const weightCeilingTon = Number.isFinite(Number(tarif_?.weightCeilingTon))
    ? Number(tarif_.weightCeilingTon)
    : 20;
  const tarif = isJoursOuvrables(loadDate, holidayDateSet)
    ? tarif_.ouvrable
    : tarif_.special;

  if (netTon <= weightCeilingTon) {
    return Math.round(Number(tarif.minimumHt || 0));
  }
  return Math.round(netTon * Number(tarif.rateHtPerTon || 0));
}

function resolveSupplementaryTaxAmount({ notes, loadTsMeta, tsInlineTotal, amountTsFromLoad }) {
  if (Number(tsInlineTotal) > 0) {
    return Math.round(Number(tsInlineTotal));
  }
  const fromMeta = Number(loadTsMeta?.totalAmount);
  if (Number.isFinite(fromMeta) && fromMeta > 0) {
    return Math.round(fromMeta);
  }
  const fromNotes = extractTsMetaFromNotes(notes)?.totalAmount;
  if (Number.isFinite(Number(fromNotes)) && Number(fromNotes) > 0) {
    return Math.round(Number(fromNotes));
  }
  const fromLoad = Number(amountTsFromLoad);
  if (Number.isFinite(fromLoad) && fromLoad > 0) {
    return Math.round(fromLoad);
  }
  return 0;
}

/**
 * Client preview aligned with server/utils/billAmountCalculator.js.
 */
export function computeLoadBillAmountPreview({
  netWeightKg,
  tariffInstant,
  notes,
  loadTsMeta,
  tsInlineTotal = 0,
  amountTsFromLoad = 0,
  tarifPoids,
  tvaRate,
  holidayDateSet
}) {
  const tarif_ = tarifPoids || DEFAULT_TARIF_POIDS;
  const rate = Number.isFinite(Number(tvaRate)) && Number(tvaRate) >= 0
    ? Number(tvaRate)
    : DEFAULT_TVA_RATE;
  const tvaFraction = rate / (1 + rate);
  const instant = tariffInstant instanceof Date ? tariffInstant : new Date(tariffInstant);

  const amountHt = calculateMontantHt(netWeightKg, instant, holidayDateSet, tarif_);
  const amountTs = resolveSupplementaryTaxAmount({
    notes,
    loadTsMeta,
    tsInlineTotal,
    amountTsFromLoad
  });
  const amountTva = Math.round(amountHt * tvaFraction);
  const amountHtExclTva = Math.round(amountHt - amountTva);
  const amountTtc = amountHt + amountTs;
  const netTon = Number(netWeightKg || 0) / 1000;
  const isOuvrable = isJoursOuvrables(instant, holidayDateSet);
  const appliedTarif = isOuvrable ? tarif_.ouvrable : tarif_.special;
  const ceilingTon = Number.isFinite(Number(tarif_.weightCeilingTon))
    ? Number(tarif_.weightCeilingTon)
    : 20;

  return {
    netTon: Number.isFinite(netTon) && netTon > 0 ? netTon : 0,
    amountHt: amountHtExclTva,
    amountTva,
    amountTtc,
    amountTs,
    isOuvrable,
    ceilingTon,
    minHt: Number(appliedTarif.minimumHt || 0),
    rateHtPerTon: Number(appliedTarif.rateHtPerTon || 0)
  };
}

/** Build holiday Set from API keys array. */
export function buildHolidayDateSet(holidayDateKeys) {
  if (!Array.isArray(holidayDateKeys) || holidayDateKeys.length === 0) {
    return new Set();
  }
  return new Set(holidayDateKeys.map((k) => String(k).trim()).filter(Boolean));
}
